import {defineStore} from "pinia";
import {FetchRequest} from "@/helpers/fetch-request";
import {useAuthStore} from "@/stores/auth.store";
import {useNotify} from "nvd-u/composables/Notifiy";
import router from "../router/routes";
import {formatErrorMessage} from "@/helpers/error-message";

export const MONTHLY = 'Billed monthly'
export const ANNUALLY = 'Billed annually'

export const useBillingStore = defineStore('billing', {
    state: () => ({
        billingInfoReq: new FetchRequest('billing_info'),
        stripeProducts: new FetchRequest('get_products'),
        verifyCouponReq: new FetchRequest('check_coupon', 'POST'),
        changePlanReq: new FetchRequest('upgrade_subscription', 'POST').withProps({silent: true}),
        checkoutReq: new FetchRequest('create_checkout', 'POST').withProps({silent: true}),
        cancelSubscriptionReq: new FetchRequest('cancel_subscription', 'POST').withProps({silent: true}),
        updateInvoiceReq: new FetchRequest('/update_invoice', 'POST'),
        cancelSubscriptionModal: false,
        updateInvoiceModal: false,
        viewInvoiceModal: false,
        choices: [MONTHLY, ANNUALLY],
        choice: ANNUALLY,
        invoice: {},
        selectedPlan: null,
        coupon: ''
    }),
    getters: {
        invoices() {
            return this.billingInfoReq?.data?.invoices || []
        },
        subscriptionPlans() {
            if (!this.stripeProducts.data?.products) return []
            let products = this.stripeProducts.data?.products
            let plans = []
            products.forEach(product => {
                product.prices?.forEach(price => plans.push(price))
            })
            return plans
        },
        currentPlan() {
            return this.subscriptionPlans.find(p => p.stripe_id === this.billingInfoReq.data?.plan_id)
        },
        currentPlanTitle() {
            return this.currentPlan?.name || this.billingInfoReq?.data?.subscription || this.billingInfoReq?.data?.status?.toUpperCase()
        },
        currentPlanStatus() {
            return this.billingInfoReq.data?.status
        },
        hasFreeAccount() {
            return ['trialing', 'free'].includes(this.billingInfoReq.data?.status?.toLowerCase())
        },
        hasActiveSub() {
            return !this.hasFreeAccount && this.billingInfoReq?.data?.status?.toLowerCase() === 'active'
        },
        getActionTitle: (store: any) => (plan) => {
            if (store.currentPlan?.stripe_id === plan?.stripe_id) return 'plan_subscribed'
            if (store.hasFreeAccount || store.currentPlan?.price <= plan?.price) return 'plan_upgrade'
            else if (store.currentPlan?.price > plan?.price || plan?.name === 'Free') return 'plan_downgrade'
            return 'plan_continue'
        },
        modifyPaymentInfoOrCancelSubscriptionEligible() {
            return this.billingInfoReq.data?.payment_gateway?.toLowerCase() === 'stripe' && this.billingInfoReq.data?.subscription_id
        },
        isTeamPlan() {
            const auth = useAuthStore()
            return this.currentPlan?.name?.toLowerCase()?.includes('team') || !!auth.user?.admin || (auth.user?.membersLimit > 1)
        },
        needsPaymentRecovery() {
            return ['past_due', 'unpaid', 'incomplete'].includes(this.currentPlanStatus?.toLowerCase())
        },
        userAlreadySubscribed() {
            return this.billingInfoReq.data?.payment_gateway?.toLowerCase() === 'stripe' &&
                !!this.billingInfoReq.data?.subscription_id &&
                !['canceled', 'incomplete_expired'].includes(this.currentPlanStatus?.toLowerCase())
        },
        isPartOfTeam() {
            return this.billingInfoReq.data?.payment_gateway?.toLowerCase() === 'team'
        },
        teamOwner(){
            return this.billingInfoReq.data?.owner
        }
    },
    actions: {
        loadBillingInfo() {
            return this.billingInfoReq.send()
        },
        updatePaymentInfo() {
            window.location.href = this.billingInfoReq.data?.payment_url
        },
        async cancelSubscription(payload) {
            const notify = useNotify()
            const request = this.cancelSubscriptionReq
            const data = {subscription: this.billingInfoReq.data?.subscription_id}
            payload.forEach(q => {
                data[q.Q] = {answer: q.A, more_info: q.more_info}
            })
            try {
                await request.send({body: JSON.stringify(data)})
                if (this.cancelSubscriptionReq !== request) return
                this.cancelSubscriptionModal = false
                notify.success('Your subscription will end at the end of the paid billing period.')
                await useAuthStore().reFetchUser()
            } catch (error) {
                if (this.cancelSubscriptionReq !== request) return
                notify.error('Cancel subscription failed', formatErrorMessage(error))
            }
        },
        updateInvoice(invoice) {
            const notify = useNotify()
            const request = this.updateInvoiceReq
            request.config.body = JSON.stringify(invoice)
            request.send()
                .then(() => {
                    if (this.updateInvoiceReq !== request) return
                    notify.success('Invoice updated successfully')
                    this.loadBillingInfo()
                })
                .finally(() => {
                    if (this.updateInvoiceReq === request) this.updateInvoiceModal = false
                })
        },
        async changePlan() {
            if (!this.selectedPlan || this.changePlanReq.loading || this.checkoutReq.loading ||
                this.getActionTitle(this.selectedPlan) === 'plan_subscribed') return
            if (this.selectedPlan.name === 'Free') {
                this.selectedPlan = null
                this.cancelSubscriptionModal = true
                return
            }
            const notify = useNotify()
            const changeRequest = this.changePlanReq
            const checkoutRequest = this.checkoutReq
            try {
                if (this.userAlreadySubscribed) {
                    const data = {
                        subscription: this.billingInfoReq.data.subscription_id,
                        plan: this.selectedPlan.stripe_id,
                        ...(this.coupon ? {coupon: this.coupon} : {})
                    }
                    await changeRequest.send({body: JSON.stringify(data)})
                    if (this.changePlanReq !== changeRequest) return
                    this.selectedPlan = null
                    notify.success('Plan changed successfully.')
                    await useAuthStore().reFetchUser()
                    await router.replace('/account/billing')
                } else {
                    const data = {
                        mode: 'subscription',
                        line_items: JSON.stringify([{price: this.selectedPlan.stripe_id, quantity: 1}]),
                        success_url: `${window.location.origin}/account/billing?payment=success`,
                        cancel_url: `${window.location.origin}/account/billing?payment=fail`,
                        ...(this.coupon ? {coupon: this.coupon} : {})
                    }
                    const response: any = await checkoutRequest.send({body: JSON.stringify(data)})
                    if (this.checkoutReq !== checkoutRequest) return
                    window.location.href = response.session_url
                }
            } catch (error) {
                if (this.changePlanReq !== changeRequest || this.checkoutReq !== checkoutRequest) return
                notify.error('Plan change failed', formatErrorMessage(error))
            }
        }
    }
})
